/**
 * Android · mounted hidden input + session 选图
 *
 * 用于：
 * - `PortfolioFilePickerHost.pick`（开弹窗前）
 * - `usePortfolioScreenshotImages.pickMoreViaSession`（弹窗内 +Upload）
 *
 * 与 detached focus fallback 的区别：
 * - input 常驻 React 树（ref），非 `document.createElement`
 * - 使用 `runPortfolioFilePickSession`，支持 visibility/blur 取消检测
 * - **必须在 `clickHiddenFileInput` 成功后** 调用 `activateCancelDetection()`
 *
 * @param onAbortReady Host 用其注册 abort，以便 `resetPickState` 打断卡死 session
 */
import type React from "react";
import { clickHiddenFileInput } from "@/src/lib/bridge/prepareFileInput";
import { getBridge } from "@/src/lib/bridge/bridge";
import type { PortfolioUploadType } from "@components/PortfolioScreenshotModal/types";
import {
  resolvePortfolioFilePickCancelDetection,
  resolvePortfolioPickConfig,
} from "./portfolioPickConfig";
import { resolvePortfolioInAppPlatformFlags } from "./portfolioInAppPlatform";
import { runPortfolioFilePickSession } from "./runPortfolioFilePickSession";

export type PickPortfolioFilesFromMountedInputParams = {
  uploadType: PortfolioUploadType;
  inputRef: React.RefObject<HTMLInputElement | null>;
  settleFiles: (files: File[]) => Promise<File[] | null>;
  onAbortReady?: (abort: () => void) => void;
};

export async function pickPortfolioFilesFromMountedInput({
  uploadType,
  inputRef,
  settleFiles,
  onAbortReady,
}: PickPortfolioFilesFromMountedInputParams): Promise<File[] | null> {
  const input = inputRef.current;
  if (!input) {
    return null;
  }

  const bridge = getBridge();
  const platformFlags = resolvePortfolioInAppPlatformFlags();
  const { preset, cancelPollMs } = resolvePortfolioPickConfig(
    uploadType,
    platformFlags,
  );

  input.value = "";

  const { promise, abort, activateCancelDetection } =
    runPortfolioFilePickSession({
      input,
      cancelPollMs,
      cancelDetection: resolvePortfolioFilePickCancelDetection(platformFlags),
      settleFiles,
    });
  onAbortReady?.(abort);

  try {
    await clickHiddenFileInput(bridge, inputRef, preset);
    activateCancelDetection();
    return await promise;
  } catch {
    abort();
    return null;
  } finally {
    onAbortReady?.(() => {});
  }
}
